import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as NewGridPeriodCreators } from '~/redux/ducks/newGridPeriod';
import { Creators as ToastrMessageActions } from '~/redux/ducks/toastrMessage';

import {
  ButtonGridPeriodIcon,
} from '../../../Components';

import {
  Container,
  ModalBox,
  Content,
  StyledDiv,
  ModalFooter,
  CampaignItem,
  CampaignUsageInfo,
  CampaignUsageTitle,
  CampaignUsageGrid,
  CampaignUsageCard,
  CampaignUsageWarning,
  CampaignUsageLabel,
  CampaignUsageCount,
  CampaignUsageMissing,
  ButtonContainer,
  CampaignPreviewItem,
  CancelButton,
  SaveButton,
} from './styles';
import SelectCampaignType from './Components/SelectCampaignType';

function ModalScheduleConfiguration({
  showModalScheduleConfiguration,
  closeModalScheduleConfiguration,
  scheduleConfiguration,
  errorMessage,
  saveScheduleConfiguration,
  periodSelected,
  isEditable = false,
}) {
  const [scheduleConfig, setScheduleConfig] = useState(new Map());
  const [selectedItem, setSelectedItem] = useState(null);
  const { zone, campaigns } = scheduleConfiguration;

  const campaignTotalLength = useMemo(
    () => Object.keys(campaigns).reduce(
      (total, campaignType) => total + (campaigns[campaignType] && campaigns[campaignType].content ? campaigns[campaignType].content.length : 0), 
      0
    ),
    [campaigns]
  );

  // Calculate available campaign types (those with remaining campaigns)
  const availableCampaignTypes = useMemo(() => {
    // Calculate current usage from scheduleConfig
    const campaignUsage = { 'NOT_AB_PROBE': 0 };
    Object.keys(campaigns).forEach(campaignType => {
      campaignUsage[campaignType] = 0;
    });
    
    // Count used campaigns from scheduleConfig
    scheduleConfig.forEach((items) => {
      items.forEach(item => {
        if (item.type && campaignUsage[item.type] !== undefined) {
          campaignUsage[item.type]++;
          if(item.type === 'PROBE' && !item.isAbTest) {
            campaignUsage['NOT_AB_PROBE']++;
          }
        }
      });
    });

    const probeCampaignsLength = campaigns.PROBE && campaigns.PROBE.content ? 
      campaigns.PROBE.content.filter(campaign => campaign.type !== 'TEST_AB').length : 0;
    
    return Object.keys(campaigns)
      .map(campaignType => ({
        value: campaignType,
        label: campaignType,
        remaining: campaigns[campaignType] && campaigns[campaignType].content ? 
          campaigns[campaignType].content.length - (campaignUsage[campaignType] || 0) : 0,
        disabled: (campaigns?.[campaignType]?.content?.length ? 
          campaigns[campaignType].content.length - (campaignUsage[campaignType] || 0) : 0) <= 0 || 
        campaignType === 'PROBE' && probeCampaignsLength === campaignUsage['NOT_AB_PROBE']
      }));
  }, [campaigns, scheduleConfig]);

  // Calculate campaign usage statistics
  const campaignUsageStats = useMemo(() => {
    const stats = {};
    
    // Initialize stats for each campaign type
    Object.keys(campaigns).forEach(campaignType => {
      const totalAvailable = campaigns[campaignType] && campaigns[campaignType].content ? 
        campaigns[campaignType].content.length : 0;
      stats[campaignType] = {
        totalAvailable,
        used: 0,
        remaining: totalAvailable,
        isWarning: false
      };
    });
    
    // Count used campaigns from scheduleConfig
    scheduleConfig.forEach((items) => {
      items.forEach(item => {
        if (item.type && stats[item.type]) {
          stats[item.type].used++;
          stats[item.type].remaining = stats[item.type].totalAvailable - stats[item.type].used;
        }
      });
    });
    
    // Check for warnings
    Object.keys(stats).forEach(campaignType => {
      stats[campaignType].isWarning = stats[campaignType].remaining > 0;
    });
    
    return stats;
  }, [campaigns, scheduleConfig]);

  // Check if there are any warnings
  const hasWarnings = Object.values(campaignUsageStats).some(stat => stat.isWarning);

  function calculateCampaignCorrelation(itemToCalculate, config = scheduleConfig) {
    if (!itemToCalculate || !itemToCalculate.type) {
      return null;
    }

    function countCampaigns(itemToCalculate, config) {
      let campaignsCount = 0;
      for (const [, items] of Array.from(config.entries()).sort(([a], [b]) => a.position - b.position)) {
        for (const item of items) {
          if (item.type === itemToCalculate.type && item.isAbTest === itemToCalculate.isAbTest) {
            if(item.id === itemToCalculate.id) {
              return campaignsCount;
            }
            campaignsCount++;
          }
        }
      }
      return campaignsCount;
    }
    

    // For other campaign types, use the existing logic
    const campaignsCount = countCampaigns(itemToCalculate, config);

    if(itemToCalculate.type === 'PROBE' && !itemToCalculate.isAbTest) {
      const probeCampaigns = campaigns.PROBE && campaigns.PROBE.content ? 
        campaigns.PROBE.content.filter(campaign => campaign.type !== 'TEST_AB') : [];
      const index = campaigns.PROBE && campaigns.PROBE.content ? 
        campaigns.PROBE.content.indexOf(probeCampaigns[campaignsCount]) : -1;
      return index;
    }
    
    return campaignsCount;
  }

  // Calculate campaign correlation for the selected item
  const campaignCorrelation = calculateCampaignCorrelation(selectedItem);

  function onCloseModal() {
    closeModalScheduleConfiguration();
  }

  function handleSaveSchedule() {
    // Convert to the API format
    const apiFormat = {};
    
    // Iterate through scheduleConfig Map
    for (const [key, items] of scheduleConfig.entries()) {
      if(items.length === 0) {
        continue;
      }

      const positionKey = key.position.toString();
      if (!apiFormat[positionKey]) {
        apiFormat[positionKey] = null;
      }

      // Check if any item has no type before adding
      if (items.find(item => item.type === null)) {
        errorMessage(
          'Erro de Validação',
          `Posição ${positionKey} sem tipo selecionado`
        );
        return;
      }
      
      // Add all items for this position
      apiFormat[positionKey] = items.map(item => ({
        type: item.type.toLowerCase(),
        percentage: item.percentage,
        ...(item.isAbTest && { isAbTest: item.isAbTest }),
      }));
    }

    // Check percentage validation for non-probe and probe positions
    const invalidPositions = [];
    const positionList = [];

    scheduleConfig.forEach((items, key) => {
      if (items.length > 0) {
        positionList.push({ position: key.position, items });
      }
    });
    
    positionList.forEach(({ position, items }) => {
      const total = items.reduce((sum, item) => sum + (item.percentage || 0), 0);
      if (Math.abs(total - 1.0) > 0.001) { // Allow small floating point differences
        invalidPositions.push(`Posição ${position}: ${Math.round(total * 100)}% (deve ser 100%)`);
      }
    });

    if (invalidPositions.length > 0) {
      errorMessage(
        'Erro de Validação de Percentuais',
        invalidPositions.join('\n')
      );
      return;
    }

    // Check if there are any empty slots (positions with no items)
    const emptySlots = [];
    for (let i = 1; i <= Object.keys(apiFormat).length; i++) {
      if (apiFormat[i.toString()] === null || apiFormat[i.toString()].length === 0) {
        emptySlots.push(i);
      }
    }
    
    if (emptySlots.length > 0) {
      errorMessage(
        'Erro de Validação',
        `Posições ${emptySlots.join(', ')} estão vazias`
      );
      return;
    }

    // Call the Redux action to save the configuration
    if (periodSelected && zone) {
      saveScheduleConfiguration(periodSelected.uuid, zone.uuid, apiFormat);
    }
  }

  const balanceItemsToCampaignLength = (config) => {
    const totalItems = Array.from(config.values()).reduce((total, items) => total + items.length, 0);    
    if (totalItems > campaignTotalLength) {
      // Remove excess items by removing entire positions that exceed the limit
      const positions = Array.from(config.keys()).sort((a, b) => b.position - a.position);
      let itemsToRemove = totalItems - campaignTotalLength;
      
      for (const pos of positions) {
        if (itemsToRemove <= 0) break;
        
        const items = config.get(pos);
        if (items.length <= 0) {
          config.delete(pos);
          continue;
        }
        if (items.length <= itemsToRemove) {
          itemsToRemove -= items.length;
          config.delete(pos);
        } else {
          for (let i = 0; i < itemsToRemove; i++) {
            items.pop();
          }
          itemsToRemove = 0;
          config.set(pos, items);
        }
      }
    } else if (totalItems < campaignTotalLength) {
      // Add items to reach the campaign total length
      const itemsToAdd = campaignTotalLength - totalItems;
      let addedItems = 0;
      
      // Create new positions to reach the campaign total length
      for (let position = 1; position <= campaignTotalLength && addedItems < itemsToAdd; position++) {
        const existingKey = Array.from(config.keys()).find(key => key.position === position);
        
        if (!existingKey) {
          // Position doesn't exist, create it with a new item
          const newKey = { position, isProbe: false };
          const newItem = { 
            id: Math.random().toString(36).substring(2, 11), 
            type: null, 
            percentage: 1,
            isAbTest: false
          };
          config.set(newKey, [newItem]);
          addedItems++;
        }
      }
    }
    
    return config;
  };

  function calculateTestAbIndexList(item, config, probeIndexes) {
    const campaignListProbe = (campaigns.PROBE || { content: [] }).content;
    const auctionCorrelationIndex = calculateCampaignCorrelation(item, config);
    const campaignAuction = (campaigns.AUCTION && campaigns.AUCTION.content && campaigns.AUCTION.content[auctionCorrelationIndex]) ? 
      campaigns.AUCTION.content[auctionCorrelationIndex] : null;
    
    if (!campaignAuction) {
      return [];
    }
    
    let realIndex = [];
    for (let i = 0; i < campaignListProbe.length; i++) {
      if (campaignListProbe[i].productUuid === campaignAuction.productUuid && !probeIndexes.has(i) && campaignListProbe[i].type === 'TEST_AB') {
        realIndex.push(i);
        if (realIndex.length === 2) {
          break;
        }
      }
    }
    return realIndex;
  }

  function validateAuctionPositions(config) {
    const internalUsedProbeIndexes = new Set();
    config.forEach((items, key) => {
      for (let idx = 0; idx < items.length; idx++) {
        const item = items[idx];
        if (item.type === 'AUCTION') {
          const testAbIndexList = calculateTestAbIndexList(item, config, internalUsedProbeIndexes);
          let positionItems = items || [];
          let auctionIdx = positionItems.findIndex(i => i.id === item.id);

          // Remove the next 2 items if they are isAbTest items related to this auction
          let existingItems = [];

          // Check the next 2 items after the auction
          for (let i = 1; i <= 2; i++) {
            if (positionItems?.[auctionIdx + i]?.isAbTest) {
              existingItems.push(positionItems[auctionIdx + i]);
            } else {
              break;
            }
          }

          // Remove the identified items
          positionItems = positionItems.filter(item => !existingItems.includes(item));

          // Create new PROBE items if there are any indexes returned
          if (testAbIndexList.length > 0) {
            const upsertProbeItems = [];

            for (const index of testAbIndexList) {
              const toUpdateProbeItem = existingItems.find(i => i.probeCampaignIndex === index || !i.probeCampaignIndex);
              if (toUpdateProbeItem) {
                upsertProbeItems.push({ ...toUpdateProbeItem, probeId: item.id, probeCampaignIndex: index });
                internalUsedProbeIndexes.add(index);
                existingItems = existingItems.filter(i => i.id !== toUpdateProbeItem.id);
                continue;
              }

              const newProbeItem = {
                id: Math.random().toString(36).substring(2, 11),
                type: 'PROBE',
                percentage: 0.1,
                isAbTest: true,
                probeCampaignIndex: index,
                probeId: item.id,
              };
              item.percentage = Math.max(item.percentage - 0.1, 0.001);
              upsertProbeItems.push(newProbeItem);
              internalUsedProbeIndexes.add(index);
            }

            // Insert all PROBE items at once after the auction
            positionItems.splice(auctionIdx + 1, 0, ...upsertProbeItems);
          }

          item.percentage = Math.max(item.percentage + (existingItems.reduce((acc, i) => acc + i.percentage, 0) || 0), 0.001);

          items = positionItems;
        }
        if (item.type === 'PROBE' && item.isAbTest) {
          const positionItems = items || [];
          const probeIdx = positionItems.findIndex(i => i.id === item.id);

          // Check if this PROBE item has a valid AUCTION parent
          const hasValidAuctionParent = item.probeId &&
            positionItems.some(auctionItem =>
              auctionItem.id === item.probeId &&
              auctionItem.type === 'AUCTION'
            );

          if (!hasValidAuctionParent) {
            items = positionItems.filter(i => i.id !== item.id);
          }
        }
      }
      config.set(key, items);
    });

    return config;
  }

  const validateCampaignTypeLimits = (config) => {
    // Count current usage by campaign type
    const campaignUsage = { 'PROBE': 0, 'TEST_AB': 0 };
    Object.keys(campaigns).forEach(campaignType => {
      campaignUsage[campaignType] = 0;
    });

    // Count used campaigns from config
    config.forEach((items) => {
      items.forEach(item => {
        if (item.type && campaignUsage[item.type] !== undefined) {
          campaignUsage[item.isAbTest ? 'TEST_AB' : item.type]++;
        }
      });
    });

    const positions = Array.from(config.keys()).sort((a, b) => b.position - a.position);

    // Check each campaign type and remove excess items from bottom to top
    Object.keys(campaigns).filter(campaignType => campaignType !== 'PROBE').forEach(campaignType => {
      const maxAllowed = campaigns[campaignType] && campaigns[campaignType].content ?
        campaigns[campaignType].content.length : 0;
      const currentUsage = campaignUsage[campaignType] || 0;

      if (currentUsage > maxAllowed) {
        const itemsToRemove = currentUsage - maxAllowed;
        let removedCount = 0;

        for (const key of positions) {
          if (removedCount >= itemsToRemove) break;

          const items = config.get(key);

          for (let i = items.length - 1; i >= 0 && removedCount < itemsToRemove; i--) {
            if (items[i].type === campaignType) {
              removedCount++;
              items[i] = { ...items[i], type: null, percentage: 1, isAbTest: false };
            }
          }

          config.set(key, items);
        }
      }
    });

    Object.keys(campaigns).filter(campaignType => campaignType === 'PROBE').forEach(campaignType => {
      const campaignContent = campaigns.PROBE && campaigns.PROBE.content || [];

      const maxAllowedProbe = campaignContent.filter(campaign => campaign.type !== 'TEST_AB').length;
      const currentUsageProbe = campaignUsage['PROBE'] || 0;

      const maxAllowedTestAb = campaignContent.filter(campaign => campaign.type === 'TEST_AB').length;
      const currentUsageTestAb = campaignUsage['TEST_AB'] || 0;

      if (currentUsageProbe > maxAllowedProbe) {
        const itemsToRemove = currentUsageProbe - maxAllowedProbe;
        let removedCount = 0;

        for (const key of positions) {
          if (removedCount >= itemsToRemove) break;

          const items = config.get(key);

          for (let i = items.length - 1; i >= 0 && removedCount < itemsToRemove; i--) {
            if (items[i].type === campaignType && !items[i].isAbTest) {
              removedCount++;
              items[i] = { ...items[i], type: null, percentage: 1, isAbTest: false };
            }
          }

          config.set(key, items);
        }
      }
      if (currentUsageTestAb > maxAllowedTestAb) {
        const itemsToRemove = currentUsageTestAb - maxAllowedTestAb;
        let removedCount = 0;

        for (const key of positions) {
          if (removedCount >= itemsToRemove) break;

          const items = config.get(key);

          for (let i = items.length - 1; i >= 0 && removedCount < itemsToRemove; i--) {
            if (items[i].type === campaignType && items[i].isAbTest) {
              removedCount++;
              items[i] = { ...items[i], type: null, percentage: 1, isAbTest: false };
            }
          }

          config.set(key, items);
        }
      }
    });

    return config;
  };

  function handleConfigValidation(config) {
    return validateCampaignTypeLimits(balanceItemsToCampaignLength(validateAuctionPositions(config)));
  }

  const handleAddNewItem = ({position}) => {
    setScheduleConfig(prev => {
      const newConfig = new Map(prev);
      const newSlot = { id: Math.random().toString(36).substring(2, 11), type: null, percentage: 1, isAbTest: false };
      
      // Find the key for this position
      const existingKey = Array.from(newConfig.keys()).find(key => key.position === position);
      
      if (existingKey) {        
        // Add new item to the existing position
        const positionItems = newConfig.get(existingKey) || [];
        newConfig.set(existingKey, [...positionItems, newSlot]);
      }
      
      return handleConfigValidation(newConfig);
    });
  };

  const handleRemoveItem = ({position}, itemId) => {
    setScheduleConfig(prev => {
      const newConfig = new Map(prev);
      
      // Find the key for this position
      const existingKey = Array.from(newConfig.keys()).find(key => key.position === position);
      
      if (existingKey) {
        const positionItems = newConfig.get(existingKey) || [];
        const itemToRemove = positionItems.find(item => item.id === itemId);
        
        // Block user removal of PROBE items - they can only be removed automatically
        if (itemToRemove && itemToRemove.isAbTest) {
          return newConfig;
        }
        
        const filteredItems = positionItems.filter(item => item.id !== itemId);
        newConfig.set(existingKey, filteredItems);
      }
      
      return handleConfigValidation(newConfig);
    });
    if(selectedItem && selectedItem.id === itemId) {
      setSelectedItem(null);
    }
  };

  const handleUpdateItem = ({position}, updateData) => {
    setScheduleConfig(prev => {
      const newConfig = new Map(prev);
      const existingKey = Array.from(newConfig.keys()).find(key => key.position === position);
      if (existingKey) {
        const positionItems = newConfig.get(existingKey) || [];
        const itemToUpdate = positionItems.find(item => item.id === updateData.id);
        if (updateData.type === null) {
          updateData = {
            id: Math.random().toString(36).substring(2, 11),
            type: null,
            percentage: 1,
            isAbTest: false
          };
        }
        
        if (itemToUpdate) {
          Object.assign(itemToUpdate, updateData);
        }
      }
      return handleConfigValidation(newConfig);
    });
  };

  const handleSwapPosition = (currentPosition, direction) => {
    setScheduleConfig(prev => {
      const newConfig = new Map(prev);
      const targetPosition = direction === 'up' ? currentPosition - 1 : currentPosition + 1;

      // Find keys for current and target positions
      const currentKey = Array.from(newConfig.keys()).find(key => key.position === currentPosition);
      const targetKey = Array.from(newConfig.keys()).find(key => key.position === targetPosition);

      if (currentKey && targetKey) {
        // Get items from both positions
        const currentItems = newConfig.get(currentKey) || [];
        const targetItems = newConfig.get(targetKey) || [];

        // Swap the items
        newConfig.set(currentKey, targetItems);
        newConfig.set(targetKey, currentItems);
      }

      return handleConfigValidation(newConfig);
    });
  };

  // Handle initial scroll to selected campaign
  useEffect(() => {
    const indexCorrelation = selectedItem && selectedItem.isAbTest ? selectedItem.probeCampaignIndex : campaignCorrelation;
    if (indexCorrelation !== null && indexCorrelation !== undefined) {
      const container = document.querySelector('[data-campaign-container]');
      if (container) {
        const selectedElement = container.children[indexCorrelation];
        if (selectedElement) {
          setTimeout(() => {
            // Calculate the position to scroll to within the container
            const containerRect = container.getBoundingClientRect();
            const elementRect = selectedElement.getBoundingClientRect();
            const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - (containerRect.height / 2) + (elementRect.height / 2);

            container.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          }, 100);
        }
      }
    }
  }, [campaignCorrelation, selectedItem]);

  // Effect to handle the loaded schedule configuration
  useEffect(() => {
    const initialConfig = new Map();
    if (scheduleConfiguration && scheduleConfiguration.configuration && Object.keys(scheduleConfiguration.configuration).length > 0) {

      // Convert the API response to the internal format
      Object.keys(scheduleConfiguration.configuration).forEach(positionStr => {
        const position = parseInt(positionStr);
        const items = scheduleConfiguration.configuration[positionStr];

        if (items && Array.isArray(items)) {

          const key = { position };
          const convertedItems = items.map(item => ({
            id: Math.random().toString(36).substring(2, 11),
            type: item.type ? item.type.toUpperCase() : 'UNKNOWN',
            percentage: item.percentage || 0,
            isAbTest: item.isAbTest || false
          }));

          initialConfig.set(key, convertedItems);
        }
      });
    }
    setScheduleConfig(handleConfigValidation(initialConfig));
  }, [scheduleConfiguration.configuration, campaigns]);

  if (!showModalScheduleConfiguration) {
    return null;
  }

  const renderCampaignList = () => {
    return (
      <>
        <div style={{ display: 'flex', gap: '20px' }}>
          {/* Left side - Schedule slots */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <h3>Planejamento Schedule</h3>
            {/* Schedule items */}
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '50px' }}>
              {Array.from(scheduleConfig.entries())
                .sort(([a], [b]) => a.position - b.position)
                .map(([key, value], index) => {
                  const sortedEntries = Array.from(scheduleConfig.entries()).sort(([a], [b]) => a.position - b.position);
                  const currentIndex = sortedEntries.findIndex(([k]) => k.position === key.position);
                  const canMoveUp = currentIndex > 0 && isEditable;
                  const canMoveDown = currentIndex < sortedEntries.length - 1 && isEditable;

                  return (
                    <React.Fragment key={`${key.position}-${index}`}>
                      {value.map((item, i) => (
                        <SelectCampaignType
                          key={`${key.position}${item.id}`}
                          position={key}
                          item={item}
                          selectedItem={selectedItem}
                          setSelectedItem={setSelectedItem}
                          onChangeItem={(updatePayload) => handleUpdateItem(key, { ...item, ...updatePayload })}
                          onAddNewItem={() => handleAddNewItem(key)}
                          onRemoveItem={() => handleRemoveItem(key, item.id)}
                          canRemoveItem={i > 0 && !item.isAbTest}
                          availableCampaignTypes={availableCampaignTypes}
                          isEditable={isEditable}
                          onSwapPosition={handleSwapPosition}
                          canMoveUp={canMoveUp}
                          canMoveDown={canMoveDown}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
          {/* Right side - Campaign preview */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '95vh' }}>
            <h3>Preview das Campanhas</h3>
            
            {/* Campaign list */}
            <div 
              data-campaign-container
              style={{ 
                flex: 1, 
                overflowY: 'hidden', 
                padding: '0',
                scrollBehavior: 'smooth',
                height: '100%',
                padding: '10px 10px'
              }}
            >
              {(campaigns[selectedItem && selectedItem.type] || { content: [] }).content.map((campaign, index) => {
                const indexCorrelation = selectedItem && selectedItem.isAbTest ? selectedItem.probeCampaignIndex : campaignCorrelation;
                const distance = Math.abs(index - indexCorrelation);
                const opacity = Math.max(1 - 0.3 * distance, 0.3);
                const scale = indexCorrelation === index ? 1.05 : Math.max(1 - 0.1 * distance, 0.9);
                const isSelected = indexCorrelation === index;
                
              return(
                 <CampaignPreviewItem 
                   key={campaign.uuid}
                   isSelected={isSelected}
                   opacity={opacity}
                   scale={scale}
                 >
                  <div style={{ fontWeight: 'bold' }}>
                    {index + 1}. {campaign.name} - {campaign.productOffers[0].CampaignProductOffer.keyword || 'Sem keyword'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    R$ {campaign.cpa || '0.00'} - {(campaign.campaignType && campaign.campaignType.label) || (campaign.campaignType && campaign.campaignType.name)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>
                    {(campaign.contract && campaign.contract.name) || 'Sem contrato'} - {(campaign.productOffers && campaign.productOffers[0] && campaign.productOffers[0].name) || 'Sem oferta'}
                  </div>
                </CampaignPreviewItem>
              )})}
              {(campaigns[selectedItem && selectedItem.type] || { content: [] }).content.length === 0 && selectedItem && selectedItem.type && (
                <CampaignItem style={{ textAlign: 'center', color: '#6c757d' }}>
                  Sem campanhas disponíveis
                </CampaignItem>
              )}
              {!selectedItem || (selectedItem && !selectedItem.type) && (
                <CampaignItem style={{ textAlign: 'center', color: '#6c757d' }}>
                  Selecione uma posição para ver as campanhas
                </CampaignItem>
              )}
            </div>
          </div>
        </div>
      </>
    )
  }


  return (
    <Container>
      <ModalBox>
        <Content>
          <StyledDiv>
            <h2>Configuração de Schedule - {zone && zone.channel && zone.channel.name} ({zone && zone.name})</h2>
            {renderCampaignList()}
          </StyledDiv>
        </Content>

        <ModalFooter>
          {/* Campaign Usage Info - Only show when editable */}
          {isEditable && (
            <CampaignUsageInfo>
              <CampaignUsageTitle>
                Status das Campanhas:
              </CampaignUsageTitle>
              <CampaignUsageGrid>
                {Object.keys(campaignUsageStats).map(campaignType => {
                  const stat = campaignUsageStats[campaignType];
                  return (
                    <CampaignUsageCard 
                      key={campaignType}
                      isWarning={stat.isWarning}
                    >
                      {stat.isWarning && (
                        <CampaignUsageWarning>⚠️</CampaignUsageWarning>
                      )}
                      <CampaignUsageLabel isWarning={stat.isWarning}>
                        {campaignType}:
                      </CampaignUsageLabel>
                      <CampaignUsageCount isWarning={stat.isWarning}>
                        {stat.used}/{stat.totalAvailable}
                      </CampaignUsageCount>
                      {stat.remaining < 0 && (
                        <CampaignUsageMissing>
                          (Faltam {Math.abs(stat.remaining)})
                        </CampaignUsageMissing>
                      )}
                    </CampaignUsageCard>
                  );
                })}
              </CampaignUsageGrid>
            </CampaignUsageInfo>
          )}
          
          <ButtonContainer>
            <CancelButton onClick={onCloseModal}>
            Cancelar
            </CancelButton>
            {isEditable && (
              <SaveButton 
                onClick={handleSaveSchedule} 
                disabled={hasWarnings}
              >
                <ButtonGridPeriodIcon />
                Salvar Configuração
              </SaveButton>
            )}
          </ButtonContainer>
        </ModalFooter>
      </ModalBox>
    </Container>
  );
}

const mapStateToProps = ({
  newGridPeriod: {
    showModalScheduleConfiguration,
    scheduleConfiguration,
    periodSelected,
  },
}) => ({
  showModalScheduleConfiguration,
  scheduleConfiguration,
  periodSelected,
});

const mapDispatchToProps = dispatch => bindActionCreators(
  {
    closeModalScheduleConfiguration: NewGridPeriodCreators.closeModalScheduleConfiguration,
    errorMessage: ToastrMessageActions.errorMessage,
    saveScheduleConfiguration: NewGridPeriodCreators.saveScheduleConfiguration,
  },
  dispatch,
);

ModalScheduleConfiguration.propTypes = {
  showModalScheduleConfiguration: PropTypes.bool.isRequired,
  closeModalScheduleConfiguration: PropTypes.func.isRequired,
  scheduleConfiguration: PropTypes.shape({
    zone: PropTypes.shape(),
    campaigns: PropTypes.object,
  }).isRequired,
  periodSelected: PropTypes.shape({
    uuid: PropTypes.string,
  }),
  errorMessage: PropTypes.func.isRequired,
  saveScheduleConfiguration: PropTypes.func.isRequired,
  isEditable: PropTypes.bool,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ModalScheduleConfiguration);